<template>
  <div id="chart-heatmap" />
</template>

<script>
import * as d3 from 'd3';
import * as chroma from 'chroma-js';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.addLicense('CH245905005');
am4core.addLicense('TL245443410');
am4core.useTheme(am4themesAnimated);
export default {
  name: 'HeatmapViz',
  props: {
    country: {
      type: String,
      required: true,
    },
  },
  watch: {
    country(newVal) {
      this.filterChart(newVal);
    },
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  data() {
    return {
      typeDefault: '',
    };
  },
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    am4core.options.autoSetClassName = true;

    dataSource.url = `${process.env.BASE_URL}data/C6_heatmap.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;

    dataSource.load();
    dataSource.events.on('done', ({ data }) => {
      // clean the data
      this.cleanData = this.formulateData(data);
      this.$emit('data-sanitized', this.cleanData);

      // create chart
      this.chart = this.createChart();
      this.setChartData(this.cleanData);
      

      // create axes
      this.categoryAxis = this.createAxisY();
      this.ageAxis = this.createAxisX();

      this.filterChart('ASEAN-6');
      this.series = this.setSeriesData();
    });
  },
  methods: {
    formulateData(rawData) {
      const cleanData = rawData.filter((d) => d.payment_type !== 'Others');
      cleanData.sort((a, b) => (a.payment_type === b.payment_type ? 0 : +(a.payment_type > b.payment_type) || -1));
      const data = cleanData.map(({ heatmapValue, ...props }) => {
        return { ...props, heatmapValue: Number(heatmapValue) };
      });

      // group by type returns a map
      const dataByCountry = d3.group(data, (d) => d.country);

      // convert Map to Array
      const dataByCountryArr = Array.from(dataByCountry, ([key, value]) => ({ key, value }));

      // sort the data
      dataByCountryArr.forEach((el) => {
        // sort by Male / Female Youths, Millennials, Gen X, Seniors
        const sortingArray = [
          'Males/Youths',
          'Males/Millennials',
          'Males/Gen Xers',
          'Males/Seniors',
          'Females/Youths',
          'Females/Millennials',
          'Females/Gen Xers',
          'Females/Seniors',
        ];

        el.value.sort((a, b) => {
          return sortingArray.indexOf(a.gender_generation) - sortingArray.indexOf(b.gender_generation);
        });

        // sort categories to the end
        // el.value.sort((a, b) => (a.payment_type === 'Others') - (b.payment_type === 'Others'));
        el.value.sort(
          (a, b) => (a.payment_type === '[bold]None of these[/]') - (b.payment_type === '[bold]None of these[/]')
        );
      });

      return dataByCountryArr;
    },
    createChart() {
      const chart = am4core.create('chart-heatmap', am4charts.XYChart);
      if (chart.logo) {
        chart.logo.disabled = true;
      }
      chart.padding(0, 0, 0, 0);
      chart.maskBullets = false;

      return chart;
    },
    createAxisY() {
      // y-axis - category axis
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());

      // logic settings
      categoryAxis.dataFields.category = 'payment_type';
      categoryAxis.renderer.inversed = true;

      // style settings
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.minGridDistance = 20;
      categoryAxis.renderer.grid.template.location = 1;

      categoryAxis.renderer.labels.template.wrap = true;
      categoryAxis.renderer.labels.template.maxWidth = 120;
      categoryAxis.renderer.labels.template.textAlign = 'end';
      categoryAxis.renderer.labels.template.fontSize = 15;
      categoryAxis.renderer.labels.template.fill = '#121212';

      if (this.$breakpoint.mobile) {
        categoryAxis.renderer.labels.template.dx = -5;
        categoryAxis.renderer.labels.template.fontSize = 10;
        categoryAxis.renderer.labels.template.maxWidth = 65;
        categoryAxis.renderer.minLabelPosition = 0.0001;
      }

      return categoryAxis;
    },
    createAxisX() {
      // x-axis - value axis
      const ageAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());

      ageAxis.dataFields.category = 'gender_generation';

      ageAxis.renderer.labels.template.adapter.add('textOutput', function sanitiseAxisLabels(text) {
        if (text) {
          if (text.includes('Females')) {
            return text.replace('Females/', '');
          }
          return text.replace('Males/', '');
        }
        return '';
      });

      ageAxis.renderer.labels.template.fontSize = 13;
      ageAxis.renderer.labels.template.fill = am4core.color('#595959');
      ageAxis.renderer.minGridDistance = 0;

      ageAxis.renderer.cellStartLocation = 0.1;
      ageAxis.renderer.cellEndLocation = 0.9;

      const femaleLabel = this.chart.createChild(am4core.Label);
      femaleLabel.text = 'Females';
      femaleLabel.fontWeight = 'bold';
      femaleLabel.fontSize = 15;
      femaleLabel.fontFamily = 'Noto Sans Bold';
      femaleLabel.align = 'center';
      femaleLabel.dx = 250;
      femaleLabel.dy = 10;
      femaleLabel.fill = '#595959';

      const maleLabel = this.chart.createChild(am4core.Label);
      maleLabel.text = 'Males';
      maleLabel.fontWeight = 'bold';
      maleLabel.fontFamily = 'Noto Sans Bold';
      maleLabel.fontSize = 15;
      maleLabel.align = 'center';
      maleLabel.dx = -150;
      maleLabel.dy = -8;
      maleLabel.fill = '#595959';

      if (this.$breakpoint.mobile) {
        ageAxis.renderer.labels.template.fontSize = 8;
        ageAxis.renderer.labels.template.dx = -3;

        maleLabel.fontSize = 12;
        maleLabel.dx = -40;
        maleLabel.dy = -14;

        femaleLabel.fontSize = 12;
        femaleLabel.dx = 100;
        femaleLabel.dy = 0;
      }

      return ageAxis;
    },
    setSeriesData() {
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.categoryX = 'gender_generation';
      series.dataFields.categoryY = 'payment_type';
      series.dataFields.value = 'heatmapValue';
      series.sequencedInterpolation = true;
      series.defaultState.transitionDuration = 3000;

      const columnTemplate = series.columns.template;

      columnTemplate.width = am4core.percent(99);
      columnTemplate.height = am4core.percent(99);
      columnTemplate.strokeWidth = 1;
      columnTemplate.strokeOpacity = 1;
      columnTemplate.stroke = am4core.color('#ffffff');

      // columnTemplate.tooltipText =
      //   "[bold]{generation} ({gender})[/]\n {heatmapValue.formatNumber('#s')}% are willing to share [text-transform: lowercase]{payment_type}[/] data";

      columnTemplate.adapter.add('tooltipText', (text, target) => {
        if (target.tooltipDataItem.dataContext.payment_type === '[bold]None of these[/]') {
          return "[bold]{generation} ({gender})[/]\n {heatmapValue.formatNumber('#s')}% prefer not to share their data";
        }
        return "[bold]{generation} ({gender})[/]\n {heatmapValue.formatNumber('#s')}% are willing to share [text-transform: lowercase]{payment_type}[/] data";
      });

      series.tooltip.getFillFromObject = false;
      // series.tooltip.background.fill = am4core.color('#002469');
      series.tooltip.background.fill = am4core.color('#ffffff');

      series.tooltip.background.cornerRadius = 4;
      series.tooltip.background.strokeOpacity = 0;
      series.tooltip.pointerOrientation = 'vertical';
      series.tooltip.animationDuration = 200;
      series.tooltip.defaultState.transitionDuration = 200;
      series.tooltip.hiddenState.transitionDuration = 200;
      series.tooltip.label.maxWidth = 200;
      series.tooltip.label.wrap = true;
      series.tooltip.label.fill = am4core.color("#121212");

      const colorsFemale = ['#FAD7E1', '#FDC4D0', '#FE94A7', '#F27783', '#F95D78'];
      const colorScaleFemale = chroma.scale(colorsFemale).domain([0, 20, 40, 60, 80, 100]);

      const colorsMale = ['#D2E4FA', '#A5CBF8', '#6BA0E0', '#437CC7', '#005EB8'];
      const colorScaleMale = chroma.scale(colorsMale).domain([0, -20, -40, -60, -80, -100]);

      columnTemplate.column.adapter.add('fill', (fill, target) => {
        if (target.dataItem) {
          if (target.dataItem.categories.categoryX.includes('Females')) {
            return am4core.color(colorScaleFemale(target.dataItem.value).css());
          }
          if (target.dataItem.categories.categoryX.includes('Males')) {
            return am4core.color(colorScaleMale(target.dataItem.value).css());
          }
          // safety color
          return am4core.color('#f7f7f7');
        }
        return fill;
      });

      const hoverState = series.columns.template.states.create('hover');
      hoverState.properties.stroke = am4core.color('#121212');
      hoverState.properties.strokeWidth = 2;

      return series;
    },
    setChartData(data) {
      const item = data.filter((d) => d.key === 'ASEAN-6');
      const init = item[0].value.map(({ heatmapValue, ...keepProps }) => {
        return { ...keepProps, heatmapValue: Number(heatmapValue * 0) };
      });

      this.chart.data = init;
    },
    filterChart(country) {
      // get data by selected type
      const selectedDataObj = this.cleanData.filter((data) => data.key === country);
      const selectedData = selectedDataObj[0].value;
      this.currentData = selectedData;

      // mutate existing value, so chart will animate the difference
      this.chart.data.forEach((d) => {
        d.heatmapValue =
          selectedData.length > 0
            ? selectedData.find((i) => i.gender_generation === d.gender_generation && i.payment_type === d.payment_type)
                .heatmapValue
            : 0;

        d.country = country;
      });

      this.setSeriesData();

      this.chart.invalidateRawData();
    },
  },
};
</script>

<style lang="scss">
#chart-heatmap {
  width: 100%;
  height: 500px;
}

tspan[style*="font-weight:bold"] {
  font-family: 'Noto Sans Bold'; 
}
</style>
